import {
  getAuth,
  signInWithEmailAndPassword,
  applyActionCode,
  checkActionCode,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../config/firebase';

/** Keys used across the registration funnel */
export const PENDING_REGISTRATION_KEY = 'pendingRegistration';

export function readPendingRegistration() {
  try {
    const raw =
      sessionStorage.getItem(PENDING_REGISTRATION_KEY) ||
      localStorage.getItem(PENDING_REGISTRATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function savePendingRegistration(data) {
  const json = JSON.stringify(data);
  sessionStorage.setItem(PENDING_REGISTRATION_KEY, json);
  localStorage.setItem(PENDING_REGISTRATION_KEY, json);
}

export function clearPendingRegistration() {
  sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
  localStorage.removeItem(PENDING_REGISTRATION_KEY);
}

/**
 * True only when the practice shell is missing (no clinicId).
 * Users may explore with a provisional clinic while registrationStatus is still pending_plan;
 * trial / plan is gated at Generate AI Consent, not by this flag.
 */
export function needsRegistrationCompletion(userData) {
  if (!userData || userData.role === 'super_admin') return false;
  if (userData.role === 'clinic_admin' && !userData.clinicId) return true;
  if (userData.registrationStatus === 'pending_plan' && !userData.clinicId) return true;
  return false;
}

/**
 * Ensure clinic_admin has a provisional clinic so Patients / forms work pre-trial.
 * Uses Firestore directly (works in local dev without the Functions emulator).
 */
export async function ensureProvisionalClinic({ practiceName = '', phone = '' } = {}) {
  const { getAuth } = await import('firebase/auth');
  const {
    doc,
    getDoc,
    setDoc,
    collection,
    serverTimestamp,
  } = await import('firebase/firestore');

  const auth = getAuth();
  const user = auth.currentUser;
  if (!user?.uid) {
    throw new Error('Sign in required to set up your practice.');
  }

  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  const userData = userSnap.exists() ? userSnap.data() : {};

  if (userData.clinicId) {
    return { clinicId: userData.clinicId, created: false };
  }

  const email = String(user.email || userData.email || '')
    .trim()
    .toLowerCase();
  if (!email) {
    throw new Error('Account email is required.');
  }

  const name =
    String(
      practiceName ||
        userData.practiceName ||
        userData.displayName ||
        user.displayName ||
        'My Practice'
    ).trim() || 'My Practice';

  const clinicRef = doc(collection(db, 'clinics'));
  const clinicId = clinicRef.id;

  await setDoc(clinicRef, {
    clinicId,
    name,
    adminEmail: email,
    adminName: name,
    email,
    phone: String(phone || userData.phone || userData.mobile || '').trim(),
    address: {},
    branding: {
      logo: '',
      primaryColor: '#3B82F6',
      secondaryColor: '#1E40AF',
      customCSS: '',
    },
    subscription: {
      status: 'incomplete',
      plan: null,
      billingCycle: null,
      monthlyConsentLimit: 0,
      consentsUsedThisMonth: 0,
    },
    freeGeneratesUsed: 0,
    freeGeneratesLimit: 3,
    settings: {
      defaultLanguage: 'en',
      supportedLanguages: ['en'],
      timezone: 'Europe/London',
      autoSendReminders: true,
      reminderDaysBefore: 2,
    },
    setupStatus: 'provisional',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    isActive: true,
    isSuspended: false,
  });

  await setDoc(
    userRef,
    {
      clinicId,
      clinicName: name,
      practiceName: userData.practiceName || name,
      displayName: userData.displayName || name,
      role: 'clinic_admin',
      registrationStatus: userData.registrationStatus || 'pending_plan',
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { clinicId, created: true };
}

/**
 * Incomplete registration resumes in the clinic shell (explore first).
 * Plan / trial is prompted from the banner or Generate AI Consent — not a signup wall.
 */
export function getRegistrationResumePath(userData, { emailVerified: _emailVerified = true } = {}) {
  if (!needsRegistrationCompletion(userData)) return null;
  return '/clinic';
}

export const REGISTRATION_PLANS = [
  { id: 'starter_v2', name: 'Starter', monthlyPrice: '£19.99', consentLimit: '100 consents / month' },
  { id: 'professional', name: 'Professional', monthlyPrice: '£39.99', consentLimit: '500 consents / month' },
  { id: 'premium', name: 'Premium', monthlyPrice: '£69.99', consentLimit: '1,500 consents / month' },
];

export function getStripePriceId(plan) {
  // Bare "starter" / "Starter" must use the current £19.99 price, never legacy £9.99.
  if (
    plan === 'starter_v2' ||
    plan === 'starter' ||
    plan === 'Starter'
  ) {
    return process.env.REACT_APP_STRIPE_CURRENT_STARTER_PRICE_ID || '';
  }
  if (plan === 'professional' || plan === 'Professional') {
    return process.env.REACT_APP_STRIPE_PROFESSIONAL_PRICE_ID || '';
  }
  if (plan === 'premium' || plan === 'Premium') {
    return process.env.REACT_APP_STRIPE_PREMIUM_PRICE_ID || '';
  }
  return '';
}

/** Browser event — EmailVerifyBanner and gates listen so UI updates immediately. */
export const EMAIL_VERIFIED_EVENT = 'smileconsent:email-verified';

function emailVerifiedSessionKey(uid) {
  return uid ? `emailVerifiedSession_${uid}` : 'emailVerifiedSession';
}

/** Persist verify success so banners hide even if Auth reload lags one tick. */
export function markEmailVerifiedSession(uid) {
  if (!uid) return;
  try {
    sessionStorage.setItem(emailVerifiedSessionKey(uid), 'true');
    localStorage.setItem(emailVerifiedSessionKey(uid), 'true');
  } catch (_) {
    /* ignore */
  }
  notifyEmailVerified({ source: 'session', uid });
}

export function hasEmailVerifiedSession(uid) {
  if (!uid) return false;
  try {
    return (
      sessionStorage.getItem(emailVerifiedSessionKey(uid)) === 'true' ||
      localStorage.getItem(emailVerifiedSessionKey(uid)) === 'true'
    );
  } catch (_) {
    return false;
  }
}

export function notifyEmailVerified(detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(EMAIL_VERIFIED_EVENT, { detail }));
  } catch (_) {
    /* ignore (SSR / non-browser) */
  }
}

/** True when Auth, Firestore, or a just-verified session mark this account verified. */
export function isClientEmailVerified(authUser, userData) {
  if (authUser?.emailVerified) return true;
  if (userData?.emailVerified === true) return true;
  const uid = authUser?.uid || userData?.uid;
  if (uid && hasEmailVerifiedSession(uid)) return true;
  return false;
}

/**
 * Email-verify gate applies only to practices that self-registered after this flag existed.
 * Existing / active clinics (no flag) are grandfathered — they are not blocked.
 */
export function mustRequireEmailVerification(userData) {
  return userData?.requiresEmailVerification === true;
}

/**
 * Force-refresh Auth emailVerified (reload + token), mirror to Firestore, notify UI.
 * Returns the best-known verified flag after sync.
 */
export async function syncEmailVerifiedFromAuth({ userData } = {}) {
  const auth = getAuth();
  let user = auth.currentUser;
  if (!user) {
    const fromDoc = userData?.emailVerified === true;
    if (fromDoc) notifyEmailVerified({ source: 'firestore' });
    return fromDoc;
  }

  try {
    await user.reload();
  } catch (_) {
    /* ignore — fall through to cached flags */
  }
  user = auth.currentUser;

  try {
    await user?.getIdToken(true);
  } catch (_) {
    /* ignore */
  }
  user = auth.currentUser;

  const verified = !!(
    user?.emailVerified ||
    userData?.emailVerified === true ||
    hasEmailVerifiedSession(user?.uid)
  );
  if (user?.emailVerified) {
    markEmailVerifiedSession(user.uid);
    try {
      await mirrorEmailVerifiedToUserDoc(user);
    } catch (_) {
      /* non-blocking */
    }
  } else if (verified) {
    notifyEmailVerified({ source: 'sync' });
  }
  return verified;
}

/** Mirror Auth emailVerified onto the Firestore users doc for funnel tracking. */
export async function mirrorEmailVerifiedToUserDoc(user) {
  if (!user?.uid || !user.emailVerified) return;
  try {
    const { getDoc } = await import('firebase/firestore');
    const ref = doc(db, 'users', user.uid);
    const snap = await getDoc(ref);
    const existing = snap.exists() ? snap.data() : {};
    if (existing.emailVerified === true && existing.emailVerifiedAt) return;

    await setDoc(
      ref,
      {
        emailVerified: true,
        emailVerifiedAt: existing.emailVerifiedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('Could not mirror emailVerified to user doc:', err?.message || err);
  }
}

/**
 * Apply email verification oobCode when present (client-side fallback).
 * Prefer completeRegistrationEmailVerification (custom token) instead.
 */
export async function applyEmailVerificationCode(oobCode) {
  if (!oobCode) return { applied: false, email: null, error: null };

  const auth = getAuth();
  let email = null;

  try {
    const info = await checkActionCode(auth, oobCode);
    email = info?.data?.email || null;
  } catch (err) {
    console.warn('checkActionCode failed:', err?.code || err?.message || err);
  }

  try {
    await applyActionCode(auth, oobCode);
    return { applied: true, email, error: null };
  } catch (err) {
    if (err.code === 'auth/invalid-action-code' || err.code === 'auth/expired-action-code') {
      return {
        applied: false,
        email,
        error: err.code,
        alreadyUsed: true,
      };
    }
    throw err;
  }
}

/**
 * Exchange verification / resume link for a checkout-ready resume session.
 * Uses server resume tokens (no Firebase custom tokens / signBlob IAM).
 *
 * When already signed in: applyActionCode on the client FIRST so this session's
 * emailVerified flips immediately, then finalize on the server without re-burning
 * the oobCode. That fixes first-click verify still showing "Please confirm email".
 */
export async function exchangeVerificationCodeForSession(oobCode) {
  const { httpsCallable } = await import('firebase/functions');
  const exchange = httpsCallable(functions, 'completeRegistrationEmailVerification');
  const auth = getAuth();
  const existing = readPendingRegistration();
  let data = {};
  let clientApplied = false;

  // Signed-in clinic tab: update local Auth before server work.
  if (auth.currentUser && oobCode) {
    const applied = await applyEmailVerificationCode(oobCode);
    clientApplied = !!applied.applied;
    if (clientApplied || applied.alreadyUsed) {
      try {
        await auth.currentUser.reload();
        await auth.currentUser.getIdToken(true);
      } catch (_) {
        /* ignore */
      }
    }
    if (auth.currentUser?.emailVerified || clientApplied) {
      markEmailVerifiedSession(auth.currentUser.uid);
      try {
        await mirrorEmailVerifiedToUserDoc(auth.currentUser);
      } catch (_) {
        /* ignore */
      }
      try {
        const result = await exchange({ clientAlreadyApplied: true });
        data = result.data || {};
      } catch (err) {
        console.warn(
          'Server finalize after client verify failed:',
          callableErrorMessage(err) || err?.message || err
        );
        // Local Auth is verified — continue; Firestore mirror already attempted.
        data = {
          alreadyComplete: true,
          email: auth.currentUser.email,
          uid: auth.currentUser.uid,
          practiceName: existing?.practiceName || auth.currentUser.displayName || '',
        };
      }
    }
  }

  // Signed-out / client apply did not verify — server consumes oobCode.
  if (!data.success && !data.alreadyComplete && !data.resumeToken) {
    const result = await exchange({ oobCode });
    data = result.data || {};
  }

  // Subscribed clinics: email is verified; no choose-plan resume token.
  if (data.alreadyComplete || !data.resumeToken) {
    let user = auth.currentUser;
    if (user) {
      try {
        await user.reload();
        await user.getIdToken(true);
      } catch (_) {
        /* ignore */
      }
      user = auth.currentUser;
      if (user?.emailVerified) {
        markEmailVerifiedSession(user.uid);
        await mirrorEmailVerifiedToUserDoc(user);
      }
    }

    const reg = {
      email: data.email || existing?.email || user?.email || '',
      password: existing?.password || '',
      practiceName:
        data.practiceName ||
        existing?.practiceName ||
        user?.displayName ||
        'My Practice',
    };
    if (reg.email) savePendingRegistration(reg);

    const verified = !!(user?.emailVerified || clientApplied || data.alreadyComplete);
    if (verified && user?.uid) markEmailVerifiedSession(user.uid);

    return {
      ok: true,
      alreadyComplete: true,
      needsSignIn: !user,
      reg,
      user: user || null,
      emailVerified: verified,
    };
  }

  return finishResumeSession(data);
}

export async function exchangeResumeTokenForSession(resumeToken) {
  const { httpsCallable } = await import('firebase/functions');
  const resume = httpsCallable(functions, 'resumeRegistrationSession');
  const result = await resume({ resumeToken });
  return finishResumeSession(result.data || {});
}

function callableErrorMessage(err) {
  if (!err) return '';
  if (err.details && typeof err.details === 'string') return err.details;
  const msg = String(err.message || err || '');
  // Strip Firebase "functions/internal" noise when server message is present
  return msg.replace(/\s*\(functions\/[a-z-]+\)\s*$/i, '').trim();
}

async function finishResumeSession(data) {
  const resumeToken = String(data.resumeToken || '').trim();
  if (!resumeToken) {
    throw new Error('Could not open choose-plan from this link. Enter your email and password to continue.');
  }

  const auth = getAuth();
  const resumeEmail = String(data.email || '').trim().toLowerCase();
  // Drop a leftover session for a different account. Keeping it makes
  // checkout compare the wrong user and reject the subscribe link.
  if (auth.currentUser) {
    try {
      await auth.currentUser.reload();
    } catch (_) {
      /* ignore */
    }
    const currentEmail = String(auth.currentUser?.email || '').trim().toLowerCase();
    const wrongAccount = resumeEmail && currentEmail && currentEmail !== resumeEmail;
    const unverified = auth.currentUser && !auth.currentUser.emailVerified;
    if (wrongAccount || unverified || !auth.currentUser) {
      try {
        const { signOut } = await import('firebase/auth');
        await signOut(auth);
      } catch (_) {
        /* ignore */
      }
    }
  }

  const existing = readPendingRegistration();
  const reg = {
    email: data.email || existing?.email || '',
    password: existing?.password || '',
    practiceName:
      data.practiceName ||
      existing?.practiceName ||
      'My Practice',
    resumeToken,
  };
  savePendingRegistration(reg);

  return {
    ok: true,
    reg,
    user: getAuth().currentUser,
    emailVerified: true,
  };
}

/**
 * Sign in with saved credentials and confirm email is verified (handles post-link redirect).
 * Verification / choose-plan reminder links exchange for a custom-token session.
 */
export async function restoreRegistrationSession({ oobCode, mode, resumeToken } = {}) {
  const reg = readPendingRegistration();
  const auth = getAuth();
  const isVerifyLink = !!(oobCode && (mode === 'verifyEmail' || !mode));
  const isResumeLink = !!resumeToken;

  if (isResumeLink) {
    try {
      return await exchangeResumeTokenForSession(resumeToken);
    } catch (err) {
      const message = callableErrorMessage(err);
      console.warn('Resume session exchange failed:', message);

      let user = auth.currentUser;
      if (user) {
        await user.reload();
        user = auth.currentUser;
        if (user?.emailVerified) {
          await mirrorEmailVerifiedToUserDoc(user);
          const resumed = {
            email: user.email,
            password: reg?.password || '',
            practiceName: reg?.practiceName || user.displayName || 'My Practice',
          };
          savePendingRegistration(resumed);
          return { ok: true, reg: resumed, user, emailVerified: true };
        }
      }

      if (reg?.email && reg?.password) {
        try {
          const signedIn = await signInAndResumeRegistration({
            email: reg.email,
            password: reg.password,
            practiceName: reg.practiceName || '',
          });
          if (signedIn.ok) return signedIn;
        } catch (_) {
          /* fall through */
        }
      }

      return {
        ok: false,
        reason: 'needs_password',
        reg: {
          email: reg?.email || '',
          password: '',
          practiceName: reg?.practiceName || '',
        },
        user: auth.currentUser,
        emailVerified: false,
        error: message,
        needsEmail: !reg?.email,
      };
    }
  }

  if (isVerifyLink) {
    try {
      return await exchangeVerificationCodeForSession(oobCode);
    } catch (err) {
      const code = err?.code || '';
      const message = callableErrorMessage(err);
      console.warn('Verification session exchange failed:', code || message);

      // Link already used / expired — continue if already signed in & verified
      let user = auth.currentUser;
      if (user) {
        await user.reload();
        user = auth.currentUser;
        if (user?.emailVerified) {
          await mirrorEmailVerifiedToUserDoc(user);
          const resumed = {
            email: user.email,
            password: reg?.password || '',
            practiceName: reg?.practiceName || user.displayName || 'My Practice',
          };
          savePendingRegistration(resumed);
          return { ok: true, reg: resumed, user, emailVerified: true };
        }
      }

      // Same browser still has password from signup — sign in without prompting
      if (reg?.email && reg?.password) {
        try {
          const signedIn = await signInAndResumeRegistration({
            email: reg.email,
            password: reg.password,
            practiceName: reg.practiceName || '',
          });
          if (signedIn.ok) return signedIn;
        } catch (_) {
          /* fall through */
        }
      }

      // Last resort: collect password (rare — expired link, no session)
      const emailGuess = reg?.email || null;
      if (emailGuess) {
        const partial = {
          email: emailGuess,
          password: '',
          practiceName: reg?.practiceName || '',
        };
        savePendingRegistration(partial);
        return {
          ok: false,
          reason: 'needs_password',
          reg: partial,
          user: auth.currentUser,
          emailVerified: false,
          error: message,
        };
      }

      return {
        ok: false,
        reason: 'needs_password',
        reg: { email: '', password: '', practiceName: '' },
        user: auth.currentUser,
        emailVerified: false,
        error: message,
        needsEmail: true,
      };
    }
  }

  // Already exchanged a verify/reminder link earlier in this browser
  if (reg?.resumeToken && reg?.email) {
    return {
      ok: true,
      reg,
      user: auth.currentUser,
      emailVerified: true,
    };
  }

  // Already signed in — allow choose-plan while unverified (soft verify)
  let user = auth.currentUser;
  if (user) {
    await user.reload();
    user = auth.currentUser;
    const resumed = {
      email: user.email,
      password: reg?.password || '',
      practiceName: reg?.practiceName || user.displayName || 'My Practice',
      ...(reg?.resumeToken ? { resumeToken: reg.resumeToken } : {}),
    };
    savePendingRegistration(resumed);
    if (user?.emailVerified) {
      await mirrorEmailVerifiedToUserDoc(user);
      return { ok: true, reg: resumed, user, emailVerified: true };
    }
    return { ok: true, reg: resumed, user, emailVerified: false };
  }

  if (!reg?.email || !reg?.password) {
    // Stay on choose-plan — never bounce to /register for abandoned signups
    return {
      ok: false,
      reason: 'needs_password',
      reg: {
        email: reg?.email || '',
        password: '',
        practiceName: reg?.practiceName || '',
      },
      user: auth.currentUser,
      needsEmail: !reg?.email,
      error: null,
    };
  }

  if (!user || user.email?.toLowerCase() !== reg.email.toLowerCase()) {
    const cred = await signInWithEmailAndPassword(auth, reg.email, reg.password);
    user = cred.user;
  }

  await user.reload();
  user = auth.currentUser;

  if (!user?.emailVerified) {
    // Resume token from email link can proceed to checkout server-side.
    if (reg.resumeToken) {
      return { ok: true, reg, user, emailVerified: true };
    }
    // Soft verify: stay on choose-plan; gate checkout / trial until verified.
    return { ok: true, reg, user, emailVerified: false };
  }

  await mirrorEmailVerifiedToUserDoc(user);

  return { ok: true, reg, user, emailVerified: true };
}

/**
 * Sign in with password to continue registration / checkout.
 * Email verification is not required here — it's gated at AI consent generate.
 */
export async function signInAndResumeRegistration({ email, password, practiceName = '' }) {
  const auth = getAuth();
  const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
  let user = cred.user;
  await user.reload();
  user = auth.currentUser;

  if (user?.emailVerified) {
    await mirrorEmailVerifiedToUserDoc(user);
  }

  const { getDoc } = await import('firebase/firestore');
  const userDoc = await getDoc(doc(db, 'users', user.uid));
  const userData = userDoc.exists() ? userDoc.data() : {};

  const reg = {
    email: user.email,
    password,
    practiceName:
      practiceName ||
      userData.displayName ||
      userData.name ||
      user.displayName ||
      'My Practice',
  };
  savePendingRegistration(reg);

  return {
    ok: true,
    reg,
    user,
    userData,
    emailVerified: !!user?.emailVerified,
  };
}
